import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Home,
  Music,
  Disc3,
  Library,
  LogOut,
  Film,
  Tv,
  Heart,
  ListMusic,
  ChevronDown,
  Settings,
  Users,
  FolderOpen,
  Menu,
  X,
  Activity,
  Download,
  Upload,
  Compass,
  Smartphone,
  Sparkles,
  CalendarPlus,
  PanelLeftClose,
  PanelLeft,
  Youtube,
} from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import { usePlayerStore } from '../stores/player'
import { playlists, playlistShares } from '../api/client'
import PlayerBar from './player/PlayerBar'
import SearchBar from './SearchBar'
import clsx from 'clsx'

interface AccordionSectionProps {
  title: string
  icon: React.ElementType
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}

function AccordionSection({ title, icon: Icon, isOpen, onToggle, children }: AccordionSectionProps) {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-dark-200 hover:bg-dark-800 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={20} />
          <span className="font-medium">{title}</span>
        </div>
        <ChevronDown
          size={18}
          className={clsx(
            'text-dark-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={clsx(
          'overflow-hidden transition-all duration-200',
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="pl-4 py-1">{children}</div>
      </div>
    </div>
  )
}

interface NavItemProps {
  to: string
  icon: React.ElementType
  label: string
  isActive: boolean
  onClick?: () => void
}

function NavItem({ to, icon: Icon, label, isActive, onClick }: NavItemProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={clsx(
        'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm',
        isActive
          ? 'bg-primary-600 text-white'
          : 'text-dark-300 hover:bg-dark-700 hover:text-dark-100'
      )}
    >
      <Icon size={18} />
      <span>{label}</span>
    </Link>
  )
}

export default function Layout() {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })
  const { stateLoaded, restoreState } = usePlayerStore()

  // Persist desktop collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(desktopCollapsed))
  }, [desktopCollapsed])

  // Restore player state on mount
  useEffect(() => {
    if (!stateLoaded) {
      restoreState()
    }
  }, [stateLoaded, restoreState])

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  // Determine which sections should be open based on current path
  const isMusicPath = ['/artists', '/albums', '/songs', '/liked'].includes(location.pathname) || location.pathname.startsWith('/playlists')
  const isTvPath = location.pathname === '/tv' || location.pathname.startsWith('/tv/')
  const isMoviesPath = location.pathname === '/movies'
  const isSettingsPath = location.pathname === '/libraries' || location.pathname === '/files' || location.pathname === '/status' || location.pathname === '/users' || location.pathname === '/importer' || location.pathname === '/upload' || location.pathname === '/downloads'

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    music: isMusicPath,
    tv: isTvPath,
    movies: isMoviesPath,
    settings: isSettingsPath,
  })

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const { data: userPlaylists } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => playlists.list(),
  })

  const { data: sharedPlaylists } = useQuery({
    queryKey: ['playlists', 'shared'],
    queryFn: () => playlistShares.listSharedWithMe(),
  })

  const closeSidebar = () => setSidebarOpen(false)

  return (
    <div className="flex h-screen h-[100dvh] flex-col overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Sidebar */}
        <aside
          className={clsx(
            'fixed inset-y-0 left-0 z-50 w-64 bg-dark-950 flex flex-col transform transition-transform duration-300 ease-in-out',
            // Mobile: controlled by sidebarOpen
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            // Desktop: controlled by desktopCollapsed
            desktopCollapsed ? 'lg:-translate-x-full' : 'lg:relative lg:translate-x-0'
          )}
        >
          {/* Logo & close/collapse buttons */}
          <div className="p-4 lg:p-6 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-primary-500">S3ND Media</h1>
            <div className="flex items-center gap-1">
              {/* Desktop collapse button */}
              <button
                onClick={() => setDesktopCollapsed(true)}
                className="p-2 text-dark-400 hover:text-dark-100 hidden lg:block"
                title="Collapse sidebar"
              >
                <PanelLeftClose size={20} />
              </button>
              {/* Mobile close button */}
              <button
                onClick={closeSidebar}
                className="p-2 text-dark-400 hover:text-dark-100 lg:hidden"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 overflow-y-auto">
            {/* Home - standalone */}
            <Link
              to="/"
              onClick={closeSidebar}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                location.pathname === '/'
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-200 hover:bg-dark-800'
              )}
            >
              <Home size={20} />
              <span className="font-medium">Home</span>
            </Link>

            {/* Discover - standalone */}
            <Link
              to="/discover"
              onClick={closeSidebar}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2',
                location.pathname === '/discover'
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-200 hover:bg-dark-800'
              )}
            >
              <Compass size={20} />
              <span className="font-medium">Discover</span>
            </Link>

            {/* YouTube - standalone */}
            <Link
              to="/youtube"
              onClick={closeSidebar}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2',
                location.pathname.startsWith('/youtube')
                  ? 'bg-red-500 text-white'
                  : 'text-dark-200 hover:bg-dark-800'
              )}
            >
              <Youtube size={20} />
              <span className="font-medium">YouTube</span>
            </Link>

            {/* Music Section */}
            <AccordionSection
              title="Music"
              icon={Music}
              isOpen={openSections.music}
              onToggle={() => toggleSection('music')}
            >
              <div className="space-y-1">
                <NavItem
                  to="/artists"
                  icon={Users}
                  label="Artists"
                  isActive={location.pathname === '/artists' || location.pathname.startsWith('/artists/')}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/albums"
                  icon={Disc3}
                  label="Albums"
                  isActive={location.pathname === '/albums' || location.pathname.startsWith('/albums/')}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/songs"
                  icon={Music}
                  label="All Songs"
                  isActive={location.pathname === '/songs'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/liked"
                  icon={Heart}
                  label="Liked Songs"
                  isActive={location.pathname === '/liked'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/unplayed"
                  icon={Sparkles}
                  label="Unplayed"
                  isActive={location.pathname === '/unplayed'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/recently-added"
                  icon={CalendarPlus}
                  label="Recently Added"
                  isActive={location.pathname === '/recently-added'}
                  onClick={closeSidebar}
                />
                {/* User Playlists */}
                {userPlaylists && userPlaylists.length > 0 && (
                  <>
                    <div className="pt-2 pb-1 px-4">
                      <span className="text-xs text-dark-500 uppercase tracking-wider">Playlists</span>
                    </div>
                    {userPlaylists.map((playlist) => (
                      <NavItem
                        key={playlist.id}
                        to={`/playlists/${playlist.id}`}
                        icon={ListMusic}
                        label={playlist.name}
                        isActive={location.pathname === `/playlists/${playlist.id}`}
                        onClick={closeSidebar}
                      />
                    ))}
                  </>
                )}
                {/* Shared Playlists (internal sharing) */}
                {sharedPlaylists && sharedPlaylists.length > 0 && (
                  <>
                    <div className="pt-2 pb-1 px-4">
                      <span className="text-xs text-dark-500 uppercase tracking-wider">Shared with Me</span>
                    </div>
                    {sharedPlaylists.map((playlist) => (
                      <NavItem
                        key={playlist.id}
                        to={`/playlists/${playlist.id}`}
                        icon={ListMusic}
                        label={playlist.name}
                        isActive={location.pathname === `/playlists/${playlist.id}`}
                        onClick={closeSidebar}
                      />
                    ))}
                  </>
                )}
              </div>
            </AccordionSection>

            {/* TV Shows Section */}
            <AccordionSection
              title="TV Shows"
              icon={Tv}
              isOpen={openSections.tv}
              onToggle={() => toggleSection('tv')}
            >
              <div className="space-y-1">
                <NavItem
                  to="/tv"
                  icon={Tv}
                  label="All Shows"
                  isActive={location.pathname === '/tv'}
                  onClick={closeSidebar}
                />
              </div>
            </AccordionSection>

            {/* Movies Section */}
            <AccordionSection
              title="Movies"
              icon={Film}
              isOpen={openSections.movies}
              onToggle={() => toggleSection('movies')}
            >
              <div className="space-y-1">
                <NavItem
                  to="/movies"
                  icon={Film}
                  label="All Movies"
                  isActive={location.pathname === '/movies'}
                  onClick={closeSidebar}
                />
              </div>
            </AccordionSection>

            {/* Settings Section */}
            <AccordionSection
              title="Settings"
              icon={Settings}
              isOpen={openSections.settings}
              onToggle={() => toggleSection('settings')}
            >
              <div className="space-y-1">
                <NavItem
                  to="/libraries"
                  icon={Library}
                  label="Libraries"
                  isActive={location.pathname === '/libraries'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/files"
                  icon={FolderOpen}
                  label="File Manager"
                  isActive={location.pathname === '/files'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/status"
                  icon={Activity}
                  label="System Status"
                  isActive={location.pathname === '/status'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/upload"
                  icon={Upload}
                  label="Upload Media"
                  isActive={location.pathname === '/upload'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/importer"
                  icon={Download}
                  label="Spotify Importer"
                  isActive={location.pathname === '/importer'}
                  onClick={closeSidebar}
                />
                <NavItem
                  to="/downloads"
                  icon={Smartphone}
                  label="Android App"
                  isActive={location.pathname === '/downloads'}
                  onClick={closeSidebar}
                />
                {user?.is_admin && (
                  <NavItem
                    to="/users"
                    icon={Users}
                    label="Users"
                    isActive={location.pathname === '/users'}
                    onClick={closeSidebar}
                  />
                )}
              </div>
            </AccordionSection>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-dark-800">
            <div className="flex items-center justify-between">
              <Link
                to="/account"
                onClick={closeSidebar}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user?.username.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-dark-100">{user?.username}</p>
                  {user?.is_admin && (
                    <p className="text-xs text-primary-400">Admin</p>
                  )}
                </div>
              </Link>
              <button
                onClick={logout}
                className="p-2 text-dark-400 hover:text-dark-100 transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-hidden bg-dark-900">
          {/* Header with search */}
          <header className="sticky top-0 z-30 flex-shrink-0 h-14 bg-dark-900/95 backdrop-blur border-b border-dark-800 px-4 lg:px-6 flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-dark-400 hover:text-dark-100 lg:hidden"
            >
              <Menu size={24} />
            </button>
            {/* Desktop expand button (when collapsed) */}
            <button
              onClick={() => setDesktopCollapsed(false)}
              className={clsx(
                'p-2 -ml-2 text-dark-400 hover:text-dark-100 hidden',
                desktopCollapsed && 'lg:block'
              )}
              title="Expand sidebar"
            >
              <PanelLeft size={24} />
            </button>
            <SearchBar className="flex-1 max-w-md" />
          </header>
          {/* Page content */}
          <div id="main-scroll-container" className="flex-1 overflow-auto pb-16 sm:pb-20 lg:pb-0">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Player bar */}
      <PlayerBar />
    </div>
  )
}
